import type { CasoData, Socia, FAQItem, Servicio, CausalOption, Oficina, Resena, Testimonio } from '../types';

/* ═══ SOCIAS ═══════════════════════════════════════════ */
export const SOCIAS: Socia[] = [
  {
    initials: 'GS',
    name: 'Gabriela Souza',
    titleTag: 'Abogada Fundadora',
    photo: '/abogadas/gabriela.jpg',
    summary: `Abogada experta en derecho de familia, civil y laboral, con destacada trayectoria en el sector público que incluye su desempeño como Abogada Auxiliar de la Corporación de Asistencia Judicial. Actualmente complementa su práctica legal con estudios de Psicología, integrando una perspectiva humana e interdisciplinaria a cada causa.`,
    bio: `Su forma de trabajo se basa en el estudio autónomo constante y en un principio fundamental: brindar un acompañamiento continuo y completo al cliente. Por ello, se caracteriza por involucrarse a fondo en cada causa, garantizando que cada persona se sienta respaldada en todas las etapas de su proceso legal.`,
    tags: ['Derecho de Familia', 'Sector Público', 'Psicología'],
  },
  {
    initials: 'VV',
    name: 'Valentina Villa',
    titleTag: 'Abogada Fundadora',
    photo: '/abogadas/valentina.jpg',
    summary: `Abogada experta en derecho laboral, familia y tramitación en juzgado de policía local, con sólida experiencia en oficinas especializadas en el área y destacada formación de posgrado: Magíster en Derecho Procesal de Familia (UCSC), Diplomado en Derecho Laboral (UDD) y Diplomado en Derecho de Familia (PUC). Reconocida por traducir el lenguaje legal en explicaciones claras y soluciones concretas para sus clientes.`,
    bio: `Su forma de trabajo se basa en algo simple pero fundamental: que sus clientes comprendan realmente lo que ocurre en su caso. Por ello, se caracteriza por entregar explicaciones claras, sin tecnicismos innecesarios, junto con un enfoque práctico orientado a soluciones concretas.`,
    tags: ['Magíster Familia UCSC', 'Diplo. Laboral UDD', 'Diplo. Familia PUC'],
  },
  {
    initials: 'PD',
    name: 'Paula Durán Mendoza',
    titleTag: 'Procuradora Judicial',
    photo: '/abogadas/paula.jpg',
    summary: `Egresada de Derecho de la Universidad Católica de la Santísima Concepción, actualmente preparando su examen de grado. Realizó su práctica profesional en las Clínicas Jurídicas UCSC —donde tramitó causas de alimentos, cumplimiento de alimentos, migración (recursos de protección) y derecho sucesorio (posesión efectiva), obteniendo la calificación máxima (7,0)— y una pasantía en la Procuraduría Fiscal de Concepción del Consejo de Defensa del Estado.`,
    bio: `Su formación la ha llevado a especializarse en Derecho Internacional de los Derechos Humanos y derechos de las mujeres, área en la que ha cursado diversos programas de perfeccionamiento. Cofundó el Centro de Estudios Mujer y Derecho de su facultad y en 2024 fue seleccionada para representar a su universidad en la Competencia Internacional Eduardo Jiménez de Aréchaga, en Costa Rica, un juicio simulado ante la Corte Interamericana de Derechos Humanos. Actualmente integra la directiva de la ONG Intransigentes como Encargada de Cohesión Sociopolítica.

En el estudio apoya la tramitación y gestión de causas, aportando capacidad de análisis, rigor en la redacción y un compromiso real con entregar un servicio profesional y humano a cada persona que confía su caso.`,
    tags: ['Derechos Humanos', 'Derechos de las Mujeres', 'Clínicas Jurídicas UCSC'],
  },
];

/* ═══ CAUSALES FINIQUITO ═══════════════════════════════ */
export const CAUSALES: CausalOption[] = [
  { value: '161', art: 'Art. 161', name: 'Necesidades de la empresa', desc: 'Indemnización por años de servicio + mes de aviso' },
  { value: '159n2', art: 'Art. 159 N°2', name: 'Renuncia voluntaria', desc: 'Solo días trabajados y vacaciones proporcionales' },
  { value: '159n4', art: 'Art. 159 N°4', name: 'Vencimiento plazo fijo', desc: 'Vacaciones proporcionales. Sin indemnización por años' },
  { value: '159n5', art: 'Art. 159 N°5', name: 'Conclusión de obra o faena', desc: 'Indemnización de 2,5 días por mes trabajado' },
  { value: '160', art: 'Art. 160', name: 'Despido por falta grave', desc: 'Solo días trabajados y vacaciones. Sin indemnización' },
];

/* ═══ SERVICIOS ════════════════════════════════════════ */
export const SERVICIOS: Servicio[] = [
  {
    num: '01',
    titulo: 'Consulta Exprés',
    desc: 'Asesoría rápida ante dudas puntuales en una causa ya existente.',
    idealPara: [
      'Personas que ya tienen una causa iniciada',
      'Dudas puntuales sobre un procedimiento judicial',
      'Revisión rápida de tu caso',
      'Orientación sobre próximos pasos',
    ],
    ejemplos: [
      '"Quiero reajustar mi pensión"',
      '"No entiendo el estado de mi causa"',
      '"¿Qué significa esta resolución?"',
      '"¿Qué puedo hacer en mi situación?"',
    ],
    duracion: '30 minutos',
    precio: '$14.990',
    precioNum: '14990',
    btnLabel: 'Contratar →',
  },
  {
    num: '02',
    titulo: 'Acompañamiento en Negociación con Empleador',
    desc: 'Asesoría y acompañamiento para negociar el término de tu relación laboral directamente con tu empleador.\n\nRevisamos tu situación, calculamos los montos que podrías exigir y te entregamos herramientas y estrategias para enfrentar la negociación de forma segura y lograr un acuerdo favorable.',
    idealPara: [
      'Renuncias negociadas',
      'Acuerdos de salida',
      'Problemas laborales previos al despido',
      'Trabajadores que quieren evitar un juicio',
    ],
    idealParaToggle: true,
    precio: '$39.990',
    precioNum: '39990',
    btnLabel: 'Contratar →',
  },
  {
    num: '03',
    titulo: 'Asesoría para Mediación',
    desc: 'Analizamos tu caso considerando tus ingresos, gastos y las necesidades tuyas y/o de tu hijo/a, para orientarte sobre cuál podría ser un acuerdo justo en mediación y qué podrías obtener eventualmente en un juicio.\n\nTe ayudamos a asistir informado/a y preparado/a a tu mediación.',
    duracion: '60 minutos',
    precio: '$29.990',
    precioNum: '29990',
    btnLabel: 'Contratar →',
  },
  {
    num: '04',
    titulo: 'Levantamiento de Empresa',
    desc: 'Diagnóstico legal para micro y pequeñas empresas, orientado a detectar errores, incumplimientos o riesgos que puedan generar fiscalizaciones, multas o demandas.\n\nTe entregamos orientación práctica para prevenir conflictos laborales y herramientas para identificar riesgos legales dentro de tu negocio.\n\nAdemás, incluye descuento preferencial en asesoría mensual.',
    precio: '$59.990',
    precioNum: '59990',
    btnLabel: 'Contratar →',
  },
  {
    num: '05',
    titulo: 'Asesoría General',
    desc: 'Asesoría jurídica personalizada en la materia que necesites, con una abogada especializada en el área correspondiente.\n\nPosteriormente, se entrega un informe con orientación jurídica y presupuesto de honorarios en caso de requerir gestión o representación.',
    duracion: '60 minutos',
    precio: '$24.990',
    precioNum: '24990',
    btnLabel: 'Contratar →',
  },
  {
    num: '06',
    titulo: 'Asesoría para Trabajadores',
    desc: 'Orientación jurídica para trabajadores que necesiten conocer sus derechos, evaluar posibles incumplimientos laborales o tomar decisiones informadas frente a problemas con su empleador.\n\nAnalizamos tu caso y te explicamos de manera clara cuáles son tus opciones y los pasos que puedes seguir.',
    idealPara: [
      'Despidos',
      'Autodespido',
      'No pago de cotizaciones',
      'Problemas con licencias o fuero',
      'Vulneración de derechos',
      'Trabajo sin contrato',
      'Acoso laboral',
      'Descuentos indebidos',
      'Dudas sobre finiquitos o remuneraciones',
    ],
    idealParaToggle: true,
    duracion: '60 minutos',
    precio: 'Gratuita',
    precioNum: '0',
    btnLabel: 'Agendar →',
  },
];

/* ═══ FAQ ══════════════════════════════════════════════ */
export const FAQ_ITEMS: FAQItem[] = [
  { question: '¿Cuánto cuesta la primera consulta?', answer: 'La primera consulta de orientación es gratuita. En ella evaluamos su situación y le indicamos las opciones legales disponibles, costos y plazos estimados.' },
  { question: '¿Atienden de forma remota?', answer: 'Sí, realizamos consultas por videollamada y gestionamos causas de forma remota en todo Chile. Utilizamos firma electrónica avanzada para documentos legales.' },
  { question: '¿Cómo puedo seguir el avance de mi caso?', answer: 'A través de nuestro Portal de Clientes en esta misma página. Le entregamos un código único al inicio de su proceso para que pueda revisar el estado en cualquier momento.' },
  { question: '¿Cuánto tiempo demora un caso laboral?', answer: 'Depende de la complejidad. Un proceso de tutela laboral puede tomar entre 3 a 8 meses. Las mediaciones ante la Inspección del Trabajo suelen resolverse en semanas.' },
  { question: '¿Trabajan con personas extranjeras?', answer: 'Sí, tenemos amplia experiencia en derecho migratorio. Asesoramos en visas, residencias, permisos de trabajo y regularización migratoria.' },
  { question: '¿Qué documentos necesito para mi primera consulta?', answer: 'Depende de la materia: en casos laborales, traiga su contrato, liquidaciones y carta de despido si la tiene. En familia, documentos de identidad y antecedentes relevantes. Si no tiene todo, no se preocupe, podemos orientarle igualmente.' },
];

/* ═══ OFICINAS ═════════════════════════════════════════ */
export const OFICINAS: Oficina[] = [
  {
    badge: 'Casa Matriz',
    name: 'Concepción Centro',
    address: "O'Higgins #940, Oficina 410, Concepción",
    hours: 'Lunes a Viernes, 09:00 – 18:00 hrs.',
    phone: '+56 9 3924 1385',
  },
  {
    badge: 'Sucursal',
    name: 'Lomas Coloradas',
    address: 'Calle René Schneider #817, Lomas Coloradas',
    hours: 'Lunes a Viernes, 09:00 – 18:00 hrs.',
    phone: '+56 9 3924 1385',
  },
];

/* ═══ CASOS DEMO ══════════════════════════════════════ */
export const CASOS_DEMO: Record<string, CasoData> = {
  'SV-2025-0142': {
    num: 'SV-2025-0142',
    tipo: 'Derecho Laboral · Tutela Laboral',
    estado: 'En Tramitación',
    timeline: [
      { label: 'Recepción del caso', date: '15 Feb 2025', status: 'done' },
      { label: 'Demanda interpuesta', date: '28 Feb 2025', status: 'done' },
      { label: 'Audiencia preparatoria', date: '20 Mar 2025', status: 'current' },
      { label: 'Audiencia de juicio', date: 'Por agendar', status: 'pending' },
      { label: 'Sentencia', date: '—', status: 'pending' },
    ],
    proxima: '20 de marzo de 2025 — 09:30 hrs. — 1° Juzgado de Letras del Trabajo, Concepción',
    nota: 'La audiencia preparatoria está confirmada. Le enviaremos la preparación por correo 48 hrs antes.',
  },
  'SV-2025-0088': {
    num: 'SV-2025-0088',
    tipo: 'Derecho de Familia · Divorcio',
    estado: 'En Mediación',
    timeline: [
      { label: 'Consulta inicial', date: '10 Ene 2025', status: 'done' },
      { label: 'Mediación familiar', date: '5 Feb 2025', status: 'current' },
      { label: 'Demanda (si aplica)', date: '—', status: 'pending' },
      { label: 'Resolución', date: '—', status: 'pending' },
    ],
    proxima: '5 de febrero de 2025 — 11:00 hrs. — Centro de Mediación, Concepción',
  },
  'SV-2026-3021': {
    num: 'SV-2026-3021',
    tipo: 'Derecho Migratorio · Visa de Residencia',
    estado: 'En Evaluación',
    timeline: [
      { label: 'Consulta inicial', date: '5 Feb 2026', status: 'done' },
      { label: 'Recopilación de documentos', date: '10 Feb 2026', status: 'done' },
      { label: 'Presentación de solicitud', date: '28 Feb 2026', status: 'current' },
      { label: 'Resolución preliminar', date: '—', status: 'pending' },
      { label: 'Aprobación final', date: '—', status: 'pending' },
    ],
    proxima: 'A la espera de respuesta del Departamento de Extranjería',
    nota: 'Su solicitud fue presentada exitosamente. El proceso de evaluación toma aproximadamente 60 días hábiles.',
  },
};

/* ═══ CHATBOT RESPONSES ════════════════════════════════ */
export const CHAT_RESPONSES: Record<string, string> = {
  laboral: '¡Entendido! En materia laboral podemos ayudarle con despidos, tutela laboral, cobro de prestaciones, acoso laboral y más.\n\n📋 La primera consulta de orientación es sin costo.\n\n¿Le gustaría agendar una consulta o prefiere contarnos brevemente su situación?',
  familia: 'En derecho de familia atendemos divorcios, pensiones alimenticias, cuidado personal, visitas y mediación.\n\n📋 La orientación inicial es gratuita.\n\n¿Quiere agendar una consulta o tiene una pregunta específica?',
  migracion: 'Asesoramos en visas, residencias temporales y definitivas, permisos de trabajo y regularización migratoria.\n\n📋 Consulta inicial sin costo.\n\n¿Desea agendar o tiene alguna consulta puntual?',
  agendar: '¡Por supuesto! Puede agendar de dos formas:\n\n1️⃣ Directamente desde nuestra web → sección "Agendar"\n2️⃣ Por WhatsApp al +56 9 3924 1385\n\n¿Le ayudo con algo más?',
  default: 'Gracias por contactarnos. Nuestras áreas principales son:\n\n🏛 Derecho Laboral\n👨‍👩‍👧 Derecho de Familia\n🌍 Derecho Migratorio\n⚖️ Derecho Civil\n\nPara una atención personalizada, le recomendamos agendar una consulta gratuita o escribirnos por WhatsApp.',
};

/* ═══ WHATSAPP ═════════════════════════════════════════ */
export const WA_NUMBER = '56939241385';
/* Texto plano, no codificado: todos los enlaces de WhatsApp pasan por
   waLink(), que codifica y añade el código de campaña cuando corresponde. */
export const WA_DEFAULT_MSG = 'Hola, me comunico desde su página web y necesito asesoría legal. ¿Podrían ayudarme?';
export const WA_FINIQUITO_MSG = 'Hola, calculé mi finiquito en su página web y quisiera revisarlo con una abogada. ¿Pueden ayudarme?';
export const CORREO_DESTINO = 'contacto@svabogadas.cl';

/* ═══ MÉTODOS DE PAGO ═════════════════════════════════ */
export const METODOS_PAGO = [
  { logo: '🏦', nombre: 'Transferencia bancaria', desc: 'Banco Estado · Cuenta Vista. Datos enviados al contratar.', activo: true },
  { logo: '💳', nombre: 'Webpay (tarjeta)', desc: 'Débito y crédito vía Transbank. Pago seguro en línea.', activo: false },
  { logo: '📱', nombre: 'Mercado Pago', desc: 'Pague con su cuenta Mercado Pago o tarjeta.', activo: false },
  { logo: '💵', nombre: 'Efectivo en oficina', desc: 'Pague presencialmente en cualquiera de nuestras oficinas.', activo: true },
];

/* ═══ EMAILJS CONFIG ══════════════════════════════════ */
export const EMAILJS_CONFIG = {
  publicKey: 'TU_PUBLIC_KEY_EMAILJS',
  serviceId: 'TU_SERVICE_ID',
  templateConsulta: 'TU_TEMPLATE_CONSULTA_ID',
  templatePostulacion: 'TU_TEMPLATE_POSTULACION_ID',
};

/* ═══ RESEÑAS DE GOOGLE ════════════════════════════════
   Prueba social de la ficha de Google Business Profile. Es lo primero que
   mira quien nos encuentra buscando y no nos conoce, por eso va arriba en la
   portada.

   CÓMO COMPLETARLO (5 minutos, una vez al mes):
     1. Abre la ficha del estudio en Google Maps.
     2. Copia la nota promedio y el total de reseñas → GOOGLE_RATING y
        GOOGLE_RESENAS_TOTAL.
     3. Copia 3 a 6 reseñas reales, textuales, con el nombre tal como aparece
        en Google → RESENAS.
     4. Pega en GOOGLE_FICHA_URL el enlace de la ficha, y en GOOGLE_RESENA_URL
        el enlace corto para dejar reseña (Google Business Profile → Pedir
        reseñas → Compartir formulario de reseña).

   IMPORTANTE: sólo reseñas reales y textuales. Mientras RESENAS esté vacío,
   la sección y el distintivo de la portada no se muestran: la página queda
   igual que antes, nunca aparecen testimonios inventados.        */

/* Enlace estable a la ficha, construido con el kgmid (el identificador del
   negocio en Google). Se omiten a propósito los parámetros de la URL larga
   que entrega el navegador (sca_esv, sei, utm_*): son de sesión, caducan y no
   corresponde dejarlos fijos en un sitio público. */
export const GOOGLE_FICHA_URL: string = 'https://www.google.com/search?kgmid=/g/11zd565xg3';
export const GOOGLE_RESENA_URL: string = '';
export const GOOGLE_RATING: number = 5.0;
export const GOOGLE_RESENAS_TOTAL: number = 9;

/* Con pocas reseñas, mostrar el total resta en vez de sumar: "5,0" solo se lee
   como excelencia, "5,0 · 4 reseñas" se lee como ficha recién creada. Pasar a
   true cuando el total sea una cifra que convenga mostrar (del orden de 15 a
   20 en adelante). */
export const MOSTRAR_TOTAL_RESENAS = false;

/* Textuales, copiadas de la ficha. La fecha se guarda como mes y año en vez de
   "hace 3 semanas": lo relativo envejece solo y en unos meses estaría mintiendo.
   Van de la más reciente a la más antigua. La reseña de Isidora Mautz
   (5 estrellas) no lleva texto, así que suma a la nota y al total pero no
   genera tarjeta. */
export const RESENAS: Resena[] = [
  {
    autor: 'Jose Quiroz Mundaca',
    fecha: 'Octubre 2026',
    estrellas: 5,
    texto: 'Excelente atención y asesoría',
  },
  {
    autor: 'Bárbara Inostroza Ayala',
    fecha: 'Octubre 2026',
    estrellas: 5,
    texto: 'Son un excelente equipo de trabajo...muy confiables solo tienes que entregar toda la información y ellas hacen todo!! Se pasaron yo me voy contenta con los resultados',
  },
  {
    autor: 'Angie contreras',
    fecha: 'Septiembre 2026',
    estrellas: 5,
    texto: 'Seca seca, logro lo que quería obtener. Nada que decir. La mejor ✨🫶',
  },
  {
    autor: 'Claudio Cifuentes',
    fecha: 'Agosto 2026',
    estrellas: 5,
    texto: '100% recomendadas muy profesionales y transparentes',
  },
  {
    autor: 'Jans Torruella',
    fecha: 'Septiembre 2026',
    estrellas: 5,
    texto: 'Gabriela y Valentina me tramitaron una causa de pensión de alimentos que nunca pensé obtener, teniendo 30 años lograron el pago de una deuda histórica por parte de mi progenitor. Las recomiendo totalmente por su calidad profesional y humana.',
  },
  {
    autor: 'Víctor Riesco',
    fecha: 'Julio 2026',
    estrellas: 5,
    texto: 'He trabajado con Valentina Villa en el aspecto laboral y de empresa y es una muy buena profesional, aunque es joven tiene las ganas para avanzar y ganar sus casos. La recomiendo a ella y su estudio.',
  },
  {
    autor: 'Jean Olate',
    fecha: 'Julio 2026',
    estrellas: 5,
    texto: 'Muy comprometidas y profesionales, acompañan y orientan en todo momento, responsables y preocupadas por sus clientes, las recomiendo en un 100%.',
  },
  {
    autor: 'Catalina Marchant',
    fecha: 'Julio 2026',
    estrellas: 5,
    texto: 'Las abogadas son súper amables y explican con lujo y detalle cada procedimiento que harán, te mantienen informado de cada paso que dan. Contestan tus dudas las veces que sea necesario para que uno las pueda entender, excelente servicio. 100% recomendadas.',
  },
];

/* ═══ TESTIMONIOS DEL SITIO ════════════════════════════
   Opiniones de clientes publicadas directamente aquí, no en Google. Se
   muestran en su propio bloque, sin estrellas ni marca de Google, para que
   nunca se confundan con las reseñas de la ficha ni con su nota: quien lee
   tiene que poder distinguir de un vistazo qué es verificable en Google y
   qué lo publicamos nosotros.

   Vacío desde septiembre de 2026: el único testimonio que había (Jans
   Torruella, pensión de alimentos) ya está publicado en Google y pasó a
   RESENAS. Se conserva la estructura por si vuelve a hacer falta.          */

export const TESTIMONIOS: Testimonio[] = [];
